import React, { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import type { MetricDef } from './metricCatalog';
import type { BusinessAnalyticsRow } from '@/pages/B2B/business-analytics/api/useBusinessAnalytics';

const MEDALS = ['🥇', '🥈', '🥉'];

interface Props {
  metric: MetricDef;
  rows: BusinessAnalyticsRow[]; // pre-sorted Top-N for this metric
  timeframeLabel: string;
  timeframeKey: string;
}

function LeaderboardCard({ metric, rows, timeframeLabel, timeframeKey }: Props): React.ReactElement {
  const cardRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, backgroundColor: '#1a1a2e' });
      const link = document.createElement('a');
      link.download = `top-${metric.key}-${timeframeKey}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Failed to export leaderboard PNG', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-[#1a1a2e] border border-gray-800 rounded-2xl p-5 flex flex-col">
      <div ref={cardRef} className="bg-[#1a1a2e] rounded-xl">
        <h3 className="text-white font-semibold text-base">{metric.label}</h3>
        <p className="text-xs text-gray-500 mb-1">{timeframeLabel}</p>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No data for this timeframe.</p>
        ) : (
          <ol className="mt-3 space-y-2">
            {rows.map((row, i) => (
              <li key={row.business_id ?? row.business_name} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 min-w-0">
                  <span className="w-6 text-center text-sm">{MEDALS[i] ?? `${i + 1}.`}</span>
                  <span className="text-sm text-gray-200 truncate">{row.business_name}</span>
                </span>
                <span className="text-sm font-semibold text-violet-300 whitespace-nowrap">
                  {metric.format(metric.accessor(row))}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
      <button
        onClick={handleDownload}
        disabled={exporting || rows.length === 0}
        className="mt-4 self-start px-3 py-1.5 text-xs font-medium text-violet-300 bg-violet-900/30 rounded-lg hover:bg-violet-900/50 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {exporting ? 'Exporting…' : 'Download'}
      </button>
    </div>
  );
}

export default LeaderboardCard;
